import { useFormik } from "formik";
import { FirmaFields } from "./StamoplysningerTop";
import { FirmaDto } from "../../../../../../interfaces/schema";
import { getPatchOperations } from "../../../../../../util/form";
import { patch } from "../../../../../../api/util";
import * as Yup from "yup";
import { validationMessages } from "../../../../../../constants/validation";
import {
  containsOnlyWhitespace,
  validateTilDatoEfterEllerSammeSomFraDato,
} from "../../../../../../util/validation";
import { getChangedFields } from "../../../../../../util/common";
import { useMutation } from "@tanstack/react-query";

const initialValues = (firma: FirmaDto) => {
  return {
    aktiv: firma.aktiv,
    beliggenhedsadresse: firma.beliggenhedsadresse,
    id: firma.id,
    tlf: firma.tlf ?? "",
    email: firma.email ?? "",
    emailSikker: firma.emailSikker ?? "",
    webAdresse: firma.webAdresse ?? "",
    advokatVirksomhed: firma.advokatVirksomhed,
    bemaerkninger: firma.bemaerkninger ?? "",
    cvrNr: firma.cvrNr,
    udenlandskVirksomhedsId: firma.udenlandskVirksomhedsId ?? "",
    ean: firma.ean ?? "",
    fakturaAfsendelse: firma.fakturaAfsendelse,
    fakturaEmail: firma.fakturaEmail ?? "",
    kommuneNr: firma.kommuneNr,
    //kredsNr: firma.kredsNr,
    navn1: firma.navn1,
    navn2: firma.navn2,
    navn3: firma.navn3,
    pnr: firma.pnr,
    //regionNr: firma.regionNr,
    retskredsNr: firma.retskredsNr,
    virksomhedsform: firma.virksomhedsform,
    selskabstype: firma.selskabstype?.toString() ?? "-1",
    harPersonrelationer: firma.harPersonrelationer,
    harUdoevende: firma.harUdoevende,
    firmatype: firma.firmatype,
    antalAnsatteUdoevendeAdvokater: firma.antalAnsatteUdoevendeAdvokater,
    debitorInfo: firma.debitorInfo,
    fakturaReference: firma.fakturaReference ?? "",
    periodeFraDato: firma.periode?.fraDato,
    periodeTilDatoInklusiv: firma.periode?.tilDatoInklusiv,
  };
};

export const useFirmaTopFormik = (
  firma: FirmaDto,
  utils: {
    setEditMode: (editmode: boolean) => void;
    invalidateData: InvalidateDataFunction<any>;
  },
) => {
  const { mutate, isPending, error, isError, reset } = useMutation({
    onSuccess: () => {
      utils.invalidateData();
      utils.setEditMode(false);
    },
    mutationFn: (body: any) => {
      return patch({
        body: JSON.stringify(getPatchOperations(body)),
        query: `firma/${firma.id}`,
      });
    },
  });

  const formik = useFormik<FirmaFields>({
    enableReinitialize: true,
    initialValues: initialValues(firma),
    validationSchema: Yup.object().shape({
      navn1: Yup.string()
        .required(validationMessages.required)
        .test(
          "navn1-is-not-only-whitespace",
          "Må ikke bestå af kun mellemrum",
          (val) => !containsOnlyWhitespace(val),
        ),
      fakturaAfsendelse: Yup.string().required(validationMessages.required),
      ean: Yup.string()
        .matches(/^[0-9]{13}$/, "EAN-nummer skal altid være 13 cifre")
        .when("fakturaAfsendelse", {
          is: "EAN",
          then: (schema) => schema.required(validationMessages.required),
          otherwise: (schema) => schema.notRequired(),
        }),
      fakturaEmail: Yup.string().when("fakturaAfsendelse", {
        is: "EMAIL",
        then: (schema) =>
          schema
            .required(validationMessages.required)
            .email(validationMessages.validEmail),
        otherwise: (schema) =>
          schema.notRequired().email(validationMessages.validEmail),
      }),
      email: Yup.string().email(validationMessages.validEmail),
      emailSikker: Yup.string().email(validationMessages.validEmail),
      periodeFraDato: Yup.date()
        .nullable()
        .required(validationMessages.required),
      periodeTilDatoInklusiv: Yup.date()
        .nullable()
        .test(
          "tilDatoInklusiv",
          "Slutdato kan ikke være før startdato",
          function (val) {
            const {
              periodeFraDato,
            }: {
              periodeFraDato: Date | undefined;
            } = this.parent;
            return validateTilDatoEfterEllerSammeSomFraDato(
              periodeFraDato,
              val,
            );
          },
        ),
      tlf: Yup.string().test(
        "Tlf must not contain only whitespace",
        "Må ikke bestå af kun mellemrum",
        (val) => !containsOnlyWhitespace(val),
      ),
      webAdresse: Yup.string().test(
        "Webadresse must not contain only whitespace",
        "Må ikke bestå af kun mellemrum",
        (val) => !containsOnlyWhitespace(val),
      ),
      bemaerkninger: Yup.string().test(
        "Bemærkninger must not contain only whitespace",
        "Må ikke bestå af kun mellemrum",
        (val) => !containsOnlyWhitespace(val),
      ),
    }),
    onSubmit: async (values) => {
      const initial = initialValues(firma);
      const changedFields = getChangedFields(values, initial);

      if (Object.keys(changedFields).length > 0) {
        const body: Record<string, any> = { ...changedFields };

        if (
          body.hasOwnProperty("selskabstype") &&
          body["selskabstype"] === "-1"
        ) {
          body["selskabstype"] = null;
        }

        Object.keys(body).forEach((key) => {
          if (body[key] === "" && initial[key] !== "") {
            body[key] = undefined;
          }
        });

        mutate(body);
      }
    },
  });

  return { formik, isLoading: isPending, error, isError, resetMutation: reset };
};
